import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import * as React from 'react';
import { KEYS } from '../../constants/keys.js';
import { A11YLabel } from '../A11YLabel/A11YLabel.js';
import { Badge } from '../Badge/Badge.js';
import { StyledButton } from './StyledButton.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["active", "aria-disabled", "aria-label", "as", "badge", "children", "className", "disabled", "elevated", "endAdornment", "hideLabel", "iconSize", "label", "onClick", "startAdornment", "tabIndex", "type", "variant"];
var Button = /*#__PURE__*/React.forwardRef(function Button2(props, ref) {
  var active = props.active,
    ariaDisabledBooleanish = props["aria-disabled"],
    ariaLabel = props["aria-label"],
    as = props.as,
    badge = props.badge,
    children = props.children,
    className = props.className,
    disabled = props.disabled,
    elevated = props.elevated,
    endAdornment = props.endAdornment,
    hideLabel = props.hideLabel,
    iconSize = props.iconSize,
    label = props.label,
    onClick = props.onClick,
    startAdornment = props.startAdornment,
    tabIndex = props.tabIndex,
    type = props.type,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? "secondary" : _props$variant,
    rest = _objectWithoutProperties(props, _excluded);
  var ariaDisabled = ariaDisabledBooleanish === void 0 ? void 0 : (ariaDisabledBooleanish === null || ariaDisabledBooleanish === void 0 ? void 0 : ariaDisabledBooleanish.toString()) === "true";
  var classes = classNames("_mntl-".concat(variant), className, {
    "_mntl-active": disabled ? false : active,
    "_mntl-disabled": disabled || ariaDisabled,
    "_mntl-elevated": elevated,
    "_mntl-hideLabel": hideLabel,
    "_mntl-anchor-tag": variant.includes("tag") && as === "a"
  });
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isFocused = _React$useState2[0],
    setIsFocused = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    isActive = _React$useState4[0],
    setIsActive = _React$useState4[1];
  var BADGE_OMIT_LIST = ["tag", "tagSuccess", "tagDanger"];
  var showBadgeCount = !!badge && (badge === null || badge === void 0 ? void 0 : badge.count) >= 0 && !BADGE_OMIT_LIST.includes(variant);
  var getBadgeVariant = function getBadgeVariant(badgeVariant) {
    if ((badge === null || badge === void 0 ? void 0 : badge.type) === "alert") return "alert";
    return badgeVariant;
  };
  var formattedBadgeLabel = showBadgeCount ? "|".concat(" ", badge.label) : "";
  var badgeAdornment = showBadgeCount && /*#__PURE__*/jsx(Badge, {
    active: disabled ? false : active || isActive,
    "aria-label": formattedBadgeLabel,
    count: badge.count,
    disabled: disabled || ariaDisabled,
    hover: isFocused,
    truncateCount: true,
    variant: getBadgeVariant(variant)
  });
  var handleFocus = function handleFocus(event) {
    if (!showBadgeCount) return;
    switch (event.type) {
      case "mouseleave":
        if (event.currentTarget === document.activeElement) {
          setIsFocused(true);
        } else {
          setIsFocused(false);
        }
        break;
      case "mouseover":
      case "focus":
        setIsFocused(true);
        break;
      case "blur":
      default:
        setIsFocused(false);
    }
  };
  var handleActive = function handleActive(event) {
    if (!showBadgeCount) return;
    switch (event.type) {
      case "mousedown":
        setIsActive(true);
        break;
      case "keydown":
        if (event.key === KEYS.SPACE) setIsActive(true);
        break;
      case "mouseup":
      case "keyup":
      default:
        setIsActive(false);
    }
  };
  return /*#__PURE__*/jsx(StyledButton, _objectSpread(_objectSpread({
    "aria-disabled": ariaDisabled,
    variant: variant,
    "aria-label": hideLabel || children ? ariaLabel || label : ariaLabel,
    as: as,
    className: classes,
    disabled: disabled,
    ref: ref,
    tabIndex: disabled ? -1 : tabIndex,
    type: as ? void 0 : type || "button",
    onMouseOver: handleFocus,
    onMouseLeave: handleFocus,
    onFocus: handleFocus,
    onBlur: handleFocus,
    onMouseDown: handleActive,
    onMouseUp: handleActive,
    onKeyDown: handleActive,
    onKeyUp: handleActive,
    onClick: ariaDisabled || onClick ? function (e) {
      if (onClick && !ariaDisabled) onClick(e);
    } : void 0
  }, rest), {}, {
    children: children ? /*#__PURE__*/jsx("span", {
      style: disabled ? {
        pointerEvents: "none"
      } : void 0,
      children: children
    }) : /*#__PURE__*/jsx(A11YLabel, {
      endAdornment: showBadgeCount ? badgeAdornment : endAdornment,
      hideLabel: hideLabel,
      iconSize: iconSize,
      label: label,
      startAdornment: startAdornment
    })
  }));
});
Button.displayName = "Button";

export { Button };
